// src/lib/pnl.js
// Pure P&L helper module — no React, no Supabase, no side effects.
// Monthly revenue/cost aggregation for the Financials page.
//
// Key rule (FA-011): Contract Revenue (accrual) and Cash Collected (cash basis)
// are always shown separately — never summed into one "revenue" figure. A job's
// contract_amount and its payment (logged as an income transaction) can otherwise
// double-count the same money.
//
// See Docs/DECISIONS_LOG.md FA-011 for the decision record.

import { parseLocalDate } from './dates'

function jobMonth(job) {
  const d = job.scheduled_date ? parseLocalDate(job.scheduled_date) : new Date(job.created_at)
  return { month: d.getMonth(), year: d.getFullYear() }
}

function jobsInMonth(jobs, month, year) {
  return (jobs || []).filter(j => { const jm = jobMonth(j); return jm.month === month && jm.year === year })
}

function txInMonth(transactions, month, year) {
  return (transactions || []).filter(t => {
    if (!t.date) return false
    const d = parseLocalDate(t.date)
    return d.getMonth() === month && d.getFullYear() === year
  })
}

/**
 * Contract Revenue (accrual) — sum of contract_amount for jobs scheduled/created that month.
 * This is the same source of truth used for job-level profitability elsewhere (jobCosting.js).
 */
export function monthContractRevenue(jobs, month, year) {
  return jobsInMonth(jobs, month, year).reduce((s, j) => s + (j.contract_amount || 0), 0)
}

/**
 * Cash Collected (cash basis) — sum of income transactions dated that month.
 * Never combined with monthContractRevenue — see FA-011.
 */
export function monthCashCollected(transactions, month, year) {
  return txInMonth(transactions, month, year).filter(t => t.type === 'income').reduce((s, t) => s + (t.amount || 0), 0)
}

/** Materials + labor + expense transactions for jobs/time entries that month. */
export function monthJobCosts(jobs, timeEntries, transactions, month, year) {
  const materialCosts = jobsInMonth(jobs, month, year)
    .reduce((s, j) => s + (j.job_costs || []).reduce((cs, c) => cs + (c.amount || 0), 0), 0)
  const laborCosts = (timeEntries || [])
    .filter(t => { const d = new Date(t.clock_in); return d.getMonth() === month && d.getFullYear() === year })
    .reduce((s, t) => { const hours = (new Date(t.clock_out) - new Date(t.clock_in)) / 3600000; return s + (hours * (t.employee?.hourly_rate || 0)) }, 0)
  const txExpenses = txInMonth(transactions, month, year).filter(t => t.type === 'expense').reduce((s, t) => s + (t.amount || 0), 0)
  return materialCosts + laborCosts + txExpenses
}

/** Overhead entries are stored 1-indexed (month: 1-12), unlike JS Date's 0-indexed month. */
export function monthOverhead(overhead, month) {
  return (overhead || []).filter(o => o.month === month + 1).reduce((s, o) => s + (o.amount || 0), 0)
}

/**
 * Net Profit uses Contract Revenue (accrual), not Cash Collected — matches how
 * job-level profit is calculated everywhere else in the app (jobCosting.js).
 */
export function monthNetProfit(contractRevenue, jobCosts, overheadAmount) {
  return contractRevenue - jobCosts - overheadAmount
}
